import { useSearchParams } from 'react-router';

import { REQUEST_STATUSES, type RequestStatus } from './types';

// Estado de la vista "Solicitudes" de la Lista Maestra en la URL (UX-NAV-02): recargar,
// compartir o abrir el enlace del aviso (D3) reproduce la vista y la solicitud abierta.
//   ?vista=solicitudes&estado=<estado|todas>&mias=1&pagina=<n>&solicitud=<id>
// Sin `vista` es la lista de documentos; cambiar de vista limpia los filtros de la otra.

export type MasterListView = 'documentos' | 'solicitudes';
export type StatusFilter = RequestStatus | 'all';

const DEFAULT_STATUS: RequestStatus = 'pendiente';
const ALL = 'todas';

function parseStatus(raw: string | null): StatusFilter {
  if (raw === ALL) {
    return 'all';
  }
  return REQUEST_STATUSES.includes(raw as RequestStatus) ? (raw as RequestStatus) : DEFAULT_STATUS;
}

function parsePage(raw: string | null): number {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

export interface RequestsUrl {
  view: MasterListView;
  status: StatusFilter;
  mine: boolean;
  page: number;
  /** Solicitud abierta en el detalle. */
  openId: string | null;
  setView: (v: MasterListView) => void;
  setStatus: (s: StatusFilter) => void;
  setMine: (mine: boolean) => void;
  setPage: (page: number) => void;
  open: (id: string) => void;
  close: () => void;
}

export function useRequestsUrl(): RequestsUrl {
  const [params, setParams] = useSearchParams();
  const update = (change: (p: URLSearchParams) => void): void => {
    const next = new URLSearchParams(params);
    change(next);
    setParams(next);
  };
  const rawId = params.get('solicitud');

  return {
    view: params.get('vista') === 'solicitudes' ? 'solicitudes' : 'documentos',
    status: parseStatus(params.get('estado')),
    mine: params.get('mias') === '1',
    page: parsePage(params.get('pagina')),
    openId: rawId !== null && /^[1-9]\d{0,17}$/.test(rawId) ? rawId : null,
    setView: (v) => setParams(v === 'solicitudes' ? new URLSearchParams({ vista: v }) : {}),
    setStatus: (s) =>
      update((p) => {
        p.delete('pagina');
        if (s === DEFAULT_STATUS) {
          p.delete('estado');
        } else {
          p.set('estado', s === 'all' ? ALL : s);
        }
      }),
    setMine: (mine) =>
      update((p) => {
        p.delete('pagina');
        if (mine) {
          p.set('mias', '1');
        } else {
          p.delete('mias');
        }
      }),
    setPage: (page) =>
      update((p) => {
        if (page <= 1) {
          p.delete('pagina');
        } else {
          p.set('pagina', String(page));
        }
      }),
    open: (id) => update((p) => p.set('solicitud', id)),
    close: () => update((p) => p.delete('solicitud')),
  };
}
